import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWeb3 } from '../context/Web3Context';
import { 
  History, ArrowDownLeft, ArrowUpRight, CreditCard, Shield,
  ExternalLink, Search, Download, RefreshCw, Activity, CheckCircle2, XCircle, ArrowLeft
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TransactionLogs = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { account, contractAddresses } = useWeb3();
  
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchTransactions();
    fetchStats();
    fetchIndexerStatus();
  }, [token, navigate]);

  const fetchIndexerStatus = async () => {
    try {
      const response = await fetch(`${API}/indexer/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setIndexerStatus(data);
      }
    } catch (error) {
      console.error('Error fetching indexer status:', error);
    }
  };

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      // Try to fetch indexed transactions first
      const indexedResponse = await fetch(`${API}/transactions/indexed?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (indexedResponse.ok) {
        const indexedData = await indexedResponse.json();
        if (indexedData.transactions && indexedData.transactions.length > 0) {
          setTransactions(indexedData.transactions);
          setLoading(false);
          return;
        }
      }
      
      // Fall back to admin transactions endpoint
      const response = await fetch(`${API}/admin/transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTransactions(data.transactions || []);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/transactions/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const [stats, setStats] = useState({ by_type: {}, total_transactions: 0 });
  const [indexerStatus, setIndexerStatus] = useState(null);

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'deposit': return <ArrowDownLeft className="w-4 h-4 text-green-400" />;
      case 'withdraw': return <ArrowUpRight className="w-4 h-4 text-red-400" />;
      case 'credit_draw': return <CreditCard className="w-4 h-4 text-blue-400" />;
      case 'credit_repay': return <CreditCard className="w-4 h-4 text-purple-400" />;
      case 'liquidation': return <Shield className="w-4 h-4 text-orange-400" />;
      default: return <History className="w-4 h-4 text-gray-400" />;
    }
  };

  const getTransactionColor = (type) => {
    switch (type) {
      case 'deposit': return 'bg-green-900/20 border-green-500/30';
      case 'withdraw': return 'bg-red-900/20 border-red-500/30';
      case 'credit_draw': return 'bg-blue-900/20 border-blue-500/30';
      case 'credit_repay': return 'bg-purple-900/20 border-purple-500/30';
      case 'liquidation': return 'bg-orange-900/20 border-orange-500/30';
      default: return 'bg-gray-900/50 border-gray-800';
    }
  };

  const formatNumber = (num) => {
    const n = parseFloat(num);
    if (n >= 1e9) return (n / 1e9).toFixed(2) + 'B';
    if (n >= 1e6) return (n / 1e6).toFixed(2) + 'M';
    if (n >= 1e3) return (n / 1e3).toFixed(2) + 'K';
    return n.toFixed(2);
  };

  const exportCSV = () => {
    const headers = ['Date', 'Type', 'User', 'Amount', 'TX Hash'];
    const rows = transactions.map(tx => [
      tx.timestamp,
      tx.type,
      tx.user,
      tx.amount,
      tx.txHash || 'N/A'
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Transactions exported');
  };

  const filteredTransactions = transactions.filter(tx => {
    const matchesSearch = tx.user?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         tx.txHash?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterType === 'all' || tx.type === filterType;
    return matchesSearch && matchesFilter;
  });

  const isOwner = account?.toLowerCase() === '0x2fb355b99b2060e42db50d99393d00f47a41d322'.toLowerCase();

  // Sample data for demo
  const sampleTransactions = [
    { id: 1, type: 'deposit', user: '0x2FB3...d322', amount: '1000000', timestamp: new Date().toISOString(), txHash: '0x1234...5678' },
    { id: 2, type: 'credit_draw', user: '0x2FB3...d322', amount: '500000', timestamp: new Date().toISOString(), txHash: '0x2345...6789' },
  ];

  const displayTransactions = transactions.length > 0 ? filteredTransactions : sampleTransactions;

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="transaction-logs">
      {/* Header */}
      <div className="bg-gray-900/50 border-b border-gray-800 px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link to="/admin-dashboard">
              <Button variant="outline" size="sm" className="border-gray-700 text-gray-400 hover:text-white">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
            </Link>
            <div className="h-8 w-px bg-gray-700" />
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <History className="w-6 h-6" />
                Transaction Audit Log
              </h1>
              <p className="text-gray-400 text-sm">Complete history of all protocol transactions</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchTransactions}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={exportCSV} className="bg-[#10b981]">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              placeholder="Search by address or tx hash..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-gray-900 border-gray-800 text-white"
            />
          </div>
          <div className="flex gap-2">
            {['all', 'deposit', 'withdraw', 'credit_draw', 'credit_repay', 'liquidation'].map(type => (
              <Button
                key={type}
                variant={filterType === type ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterType(type)}
                className={filterType === type ? 'bg-[#10b981]' : ''}
              >
                {type === 'all' ? 'All' : type.replace('_', ' ')}
              </Button>
            ))}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card className="bg-green-900/20 border-green-500/30">
            <CardContent className="pt-4 text-center">
              <ArrowDownLeft className="w-5 h-5 text-green-400 mx-auto mb-1" />
              <p className="text-lg font-bold text-white">{stats.by_type?.deposit || 0}</p>
              <p className="text-xs text-gray-400">Deposits</p>
            </CardContent>
          </Card>
          <Card className="bg-red-900/20 border-red-500/30">
            <CardContent className="pt-4 text-center">
              <ArrowUpRight className="w-5 h-5 text-red-400 mx-auto mb-1" />
              <p className="text-lg font-bold text-white">{stats.by_type?.withdraw || 0}</p>
              <p className="text-xs text-gray-400">Withdrawals</p>
            </CardContent>
          </Card>
          <Card className="bg-blue-900/20 border-blue-500/30">
            <CardContent className="pt-4 text-center">
              <CreditCard className="w-5 h-5 text-blue-400 mx-auto mb-1" />
              <p className="text-lg font-bold text-white">{stats.by_type?.credit_draw || 0}</p>
              <p className="text-xs text-gray-400">Credit Draws</p>
            </CardContent>
          </Card>
          <Card className="bg-purple-900/20 border-purple-500/30">
            <CardContent className="pt-4 text-center">
              <CreditCard className="w-5 h-5 text-purple-400 mx-auto mb-1" />
              <p className="text-lg font-bold text-white">{stats.by_type?.credit_repay || 0}</p>
              <p className="text-xs text-gray-400">Repayments</p>
            </CardContent>
          </Card>
          <Card className="bg-orange-900/20 border-orange-500/30">
            <CardContent className="pt-4 text-center">
              <Shield className="w-5 h-5 text-orange-400 mx-auto mb-1" />
              <p className="text-lg font-bold text-white">{stats.by_type?.liquidation || 0}</p>
              <p className="text-xs text-gray-400">Liquidations</p>
            </CardContent>
          </Card>
        </div>

        {/* Transaction List */}
        <Card className="bg-gray-900/50 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white">Recent Transactions</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-gray-400">Loading...</div>
            ) : displayTransactions.length === 0 ? (
              <div className="text-center py-8 text-gray-400">No transactions found</div>
            ) : (
              <div className="space-y-3">
                {displayTransactions.map((tx, idx) => (
                  <div 
                    key={idx}
                    className={`p-4 rounded-lg border ${getTransactionColor(tx.type)}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {getTransactionIcon(tx.type)}
                        <div>
                          <p className="text-white font-medium capitalize">
                            {tx.type?.replace('_', ' ')}
                          </p>
                          <p className="text-xs text-gray-400 font-mono">{tx.user}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-white font-bold">{formatNumber(tx.amount)} BETH</p>
                        <p className="text-xs text-gray-500">
                          {new Date(tx.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    {tx.txHash && (
                      <a
                        href={`https://etherscan.io/tx/${tx.txHash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1 mt-2"
                      >
                        {tx.txHash} <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Note */}
        <div className="mt-6">
          {indexerStatus && (
            <Card className="bg-gray-900/50 border-gray-800">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Activity className={`w-5 h-5 ${indexerStatus.status === 'running' ? 'text-green-400' : 'text-yellow-400'}`} />
                    <div>
                      <p className="text-white font-medium">Blockchain Event Indexer</p>
                      <p className="text-sm text-gray-400">
                        Status: <span className={indexerStatus.status === 'running' ? 'text-green-400' : 'text-yellow-400'}>
                          {indexerStatus.status === 'running' ? 'Running' : 'Stopped'}
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="text-right text-sm">
                    <p className="text-gray-400">
                      Block: <span className="text-white font-mono">{indexerStatus.last_indexed_block?.toLocaleString()}</span>
                      {indexerStatus.current_block && (
                        <span className="text-gray-500"> / {indexerStatus.current_block.toLocaleString()}</span>
                      )}
                    </p>
                    {indexerStatus.blocks_behind !== null && indexerStatus.blocks_behind > 0 && (
                      <p className="text-yellow-400 text-xs">{indexerStatus.blocks_behind} blocks behind</p>
                    )}
                    {indexerStatus.blockchain_connected ? (
                      <p className="text-green-400 text-xs flex items-center justify-end gap-1">
                        <CheckCircle2 className="w-3 h-3" /> Ethereum connected
                      </p>
                    ) : (
                      <p className="text-red-400 text-xs flex items-center justify-end gap-1">
                        <XCircle className="w-3 h-3" /> Disconnected
                      </p>
                    )}
                  </div>
                </div>
                <p className="text-gray-500 text-xs mt-3">
                  Total indexed transactions: {stats.total_transactions || 0}. 
                  Events are automatically synced from all protocol smart contracts.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default TransactionLogs;
